/**
 * AuditLog — generic reverse-chronological per-field edit trail.
 * Consumes an array of audit entries; each entry's JSONB `fieldChanges`
 * renders as a before→after diff table.
 *
 * Extracted from the byte-identical VenueAuditLog and StaffAuditLog
 * components (BON-214 slice 3).
 */
import { History } from 'lucide-react';

import { formatAuditFieldLabel, formatAuditValue } from '../../lib/auditFormatters';

export interface AuditEntry {
  id: string;
  actorUserId: string;
  actorEmail: string | null;
  /** Shown in place of the email when the caller knows the person's name. */
  actorName?: string | null;
  changedAt: string | Date;
  fieldChanges: unknown;
  /** Replaces "edited N fields", e.g. "created the campaign". */
  summary?: string;
  /** Show only the new values, as for a record's creation where there was no before. */
  hideBefore?: boolean;
}

interface Props {
  audit: AuditEntry[];
  /** How each entry's time reads. Defaults to the browser's own date and time format. */
  formatTime?: (changedAt: string | Date) => string;
}

const defaultFormatTime = (changedAt: string | Date) => new Date(changedAt).toLocaleString();

function summaryOf(entry: AuditEntry, fieldCount: number): string {
  return entry.summary ?? `edited ${fieldCount} ${fieldCount === 1 ? 'field' : 'fields'}`;
}

function isFieldChanges(v: unknown): v is Record<string, { before: unknown; after: unknown }> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export default function AuditLog({ audit, formatTime = defaultFormatTime }: Props) {
  if (audit.length === 0) {
    return (
      <div className="text-sm text-stone-500 dark:text-stone-400 italic flex items-center gap-2">
        <History size={14} /> No edits recorded yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {audit.map((entry) => {
        const changes = isFieldChanges(entry.fieldChanges) ? entry.fieldChanges : {};
        return (
          <div
            key={entry.id}
            className="border border-stone-200 dark:border-stone-700 rounded-lg p-3 bg-white dark:bg-[#1e2521]"
          >
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-sm">
                <span className="font-medium text-bark-900 dark:text-stone-100">
                  {entry.actorName || entry.actorEmail || entry.actorUserId}
                </span>
                <span className="text-stone-500 dark:text-stone-400">
                  {' '}
                  {summaryOf(entry, Object.keys(changes).length)}
                </span>
              </div>
              <time
                className="text-xs text-stone-500 dark:text-stone-400"
                dateTime={
                  typeof entry.changedAt === 'string'
                    ? entry.changedAt
                    : entry.changedAt.toISOString()
                }
              >
                {formatTime(entry.changedAt)}
              </time>
            </div>
            <table className="text-sm w-full">
              <tbody>
                {Object.entries(changes).map(([field, change]) => (
                  <tr key={field}>
                    <td className="font-medium pr-3 py-0.5 text-stone-700 dark:text-stone-300">
                      {formatAuditFieldLabel(field)}
                    </td>
                    {!entry.hideBefore && (
                      <td className="pr-3 py-0.5 font-mono text-xs text-stone-500 line-through">
                        {formatAuditValue(change.before)}
                      </td>
                    )}
                    <td
                      colSpan={entry.hideBefore ? 2 : undefined}
                      className="py-0.5 font-mono text-xs text-bark-900 dark:text-stone-100"
                    >
                      {entry.hideBefore ? '' : '→ '}
                      {formatAuditValue(change.after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
